import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Title,
  Text,
  Grid,
  Card,
  Badge,
  Select,
  SelectItem,
} from '@tremor/react';
import { Activity, AlertTriangle, TimerReset } from 'lucide-react';
import { KPICard } from '@/components/KPICard';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { api, buildQueryString } from '@/lib/api';

interface FailureClassCount {
  failure_class: string | null;
  detected: number;
  healed: number;
  escalated: number;
  noop: number;
}

interface RescueHealth {
  last_event_at: string | null;
  last_event_age_seconds: number | null;
  table_present: boolean;
  trigger_present: boolean;
  events_24h: number;
  healed_24h: number;
  escalated_24h: number;
  noop_24h: number;
  shadow_only_24h: number;
  by_class: FailureClassCount[];
}

interface RescueEventItem {
  id: number;
  processo_id: number;
  detected_at: string;
  failure_class: string | null;
  playbook: string | null;
  action_taken: string | null;
  outcome: string;
  attempt: number;
  duration_ms: number | null;
  cost_brl: number | null;
  error_message: string | null;
  shadow_mode: boolean;
}

interface RescueEventList {
  items: RescueEventItem[];
  total: number;
  limit: number;
  offset: number;
}

const OUTCOME_COLORS: Record<string, 'green' | 'red' | 'yellow' | 'slate' | 'blue'> = {
  HEALED: 'green',
  ESCALATED: 'red',
  RETRYING: 'yellow',
  ERROR: 'red',
  DIAGNOSED: 'blue',
  DETECTED: 'blue',
  NOOP: 'slate',
};

function fmtAge(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', { hour12: false });
}

export function RescuePage() {
  const [classFilter, setClassFilter] = useState<string>('');
  const [outcomeFilter, setOutcomeFilter] = useState<string>('');

  const health = useQuery<RescueHealth>({
    queryKey: ['rescue.health'],
    queryFn: async () => (await api().get('/rescue/health')).data,
    refetchInterval: 30_000,
  });

  const events = useQuery<RescueEventList>({
    queryKey: ['rescue.events', classFilter, outcomeFilter],
    queryFn: async () => {
      const qs = buildQueryString({
        days: 7,
        limit: 100,
        failure_class: classFilter || undefined,
        outcome: outcomeFilter || undefined,
      });
      return (await api().get(`/rescue/events${qs}`)).data;
    },
    refetchInterval: 30_000,
  });

  if (health.isLoading) return <LoadingSkeleton />;

  const h = health.data;
  if (!h) return <Text>Failed to load rescue health.</Text>;

  const daemonOk = h.table_present && h.trigger_present && h.events_24h > 0;
  const ageStr = fmtAge(h.last_event_age_seconds);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Title>Rescue Bot</Title>
          <Text>
            Monitor e auto-healing de processos com falha. Ver{' '}
            <a
              className="text-blue-400 underline"
              href="https://github.com/ddzodan/calcfin/blob/main/tools/rescue_bot/README.md"
              target="_blank"
              rel="noreferrer"
            >
              README
            </a>
            .
          </Text>
        </div>
        <Badge color={daemonOk ? 'green' : 'red'} icon={Activity}>
          {daemonOk ? 'daemon ativo' : 'daemon offline'}
        </Badge>
      </div>

      {/* Health KPIs */}
      <Grid numItemsMd={4} className="gap-4">
        <KPICard
          label="Eventos 24h"
          value={h.events_24h.toString()}
          hint={`último há ${ageStr}`}
        />
        <KPICard
          label="Healed 24h"
          value={h.healed_24h.toString()}
          hint={
            h.events_24h > 0
              ? `${Math.round((h.healed_24h / h.events_24h) * 100)}% sucesso`
              : undefined
          }
        />
        <KPICard
          label="Escalated 24h"
          value={h.escalated_24h.toString()}
          hint="precisa atenção humana"
        />
        <KPICard
          label="Shadow-only 24h"
          value={h.shadow_only_24h.toString()}
          hint="diagnose sem ação"
        />
      </Grid>

      {/* Infra status row */}
      <Card>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <Badge color={h.table_present ? 'green' : 'red'}>
            tabela rescue_event {h.table_present ? '✓' : '✗'}
          </Badge>
          <Badge color={h.trigger_present ? 'green' : 'red'}>
            trigger NOTIFY {h.trigger_present ? '✓' : '✗'}
          </Badge>
          <span className="text-slate-400">
            último evento:{' '}
            {h.last_event_at ? fmtTime(h.last_event_at) : '— nenhum'}
          </span>
        </div>
        {!h.table_present && (
          <div className="mt-3 rounded-md bg-red-950/40 p-3 text-sm text-red-300">
            <AlertTriangle className="inline h-4 w-4" /> A tabela{' '}
            <code>rescue_event</code> não existe. Aplique a migration:{' '}
            <code>
              sudo -u postgres psql -d pjecalc -f
              /opt/calcfin/_migrations/add_rescue_bot.sql
            </code>
          </div>
        )}
      </Card>

      {/* Per-class breakdown */}
      <Card>
        <div className="flex items-center justify-between">
          <Title>Por classe de falha (24h)</Title>
          <TimerReset className="h-4 w-4 text-slate-500" />
        </div>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-slate-400">
              <tr>
                <th className="pb-2">Classe</th>
                <th className="pb-2 text-right">Detectados</th>
                <th className="pb-2 text-right">Healed</th>
                <th className="pb-2 text-right">Escalated</th>
                <th className="pb-2 text-right">No-op</th>
              </tr>
            </thead>
            <tbody>
              {h.by_class.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-4 text-center text-slate-500">
                    Nenhum evento nas últimas 24h.
                  </td>
                </tr>
              ) : (
                h.by_class.map((c) => (
                  <tr
                    key={c.failure_class ?? '_'}
                    className="border-t border-slate-800/60"
                  >
                    <td className="py-2">
                      <code className="text-blue-300">
                        {c.failure_class ?? '(unknown)'}
                      </code>
                    </td>
                    <td className="py-2 text-right">{c.detected}</td>
                    <td className="py-2 text-right text-emerald-400">
                      {c.healed}
                    </td>
                    <td className="py-2 text-right text-red-400">
                      {c.escalated}
                    </td>
                    <td className="py-2 text-right text-slate-500">{c.noop}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Recent events */}
      <Card>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <Title>Eventos recentes (7 dias)</Title>
          <div className="flex gap-2">
            <Select
              value={classFilter}
              onValueChange={setClassFilter}
              placeholder="Todas as classes"
              className="w-56"
            >
              <SelectItem value="">Todas as classes</SelectItem>
              {h.by_class.map((c) => (
                <SelectItem
                  key={c.failure_class ?? '_'}
                  value={c.failure_class ?? ''}
                >
                  {c.failure_class ?? '(unknown)'}
                </SelectItem>
              ))}
            </Select>
            <Select
              value={outcomeFilter}
              onValueChange={setOutcomeFilter}
              placeholder="Todos outcomes"
              className="w-44"
            >
              <SelectItem value="">Todos</SelectItem>
              <SelectItem value="HEALED">HEALED</SelectItem>
              <SelectItem value="ESCALATED">ESCALATED</SelectItem>
              <SelectItem value="RETRYING">RETRYING</SelectItem>
              <SelectItem value="DIAGNOSED">DIAGNOSED</SelectItem>
              <SelectItem value="NOOP">NOOP</SelectItem>
              <SelectItem value="ERROR">ERROR</SelectItem>
            </Select>
          </div>
        </div>

        {events.isLoading ? (
          <LoadingSkeleton />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-slate-400">
                <tr>
                  <th className="pb-2">Quando</th>
                  <th className="pb-2">Processo</th>
                  <th className="pb-2">Classe</th>
                  <th className="pb-2">Playbook</th>
                  <th className="pb-2">Outcome</th>
                  <th className="pb-2">Ação</th>
                  <th className="pb-2 text-right">Tentativa</th>
                  <th className="pb-2 text-right">Duração</th>
                  <th className="pb-2">Modo</th>
                </tr>
              </thead>
              <tbody>
                {(events.data?.items ?? []).map((e) => (
                  <tr
                    key={e.id}
                    className="border-t border-slate-800/60 align-top"
                  >
                    <td className="py-2 whitespace-nowrap text-slate-400">
                      {fmtTime(e.detected_at)}
                    </td>
                    <td className="py-2">
                      <Link
                        to="/cases/$caseId"
                        params={{ caseId: String(e.processo_id) }}
                        className="text-blue-400 hover:underline"
                      >
                        #{e.processo_id}
                      </Link>
                    </td>
                    <td className="py-2">
                      <code className="text-xs">
                        {e.failure_class ?? '—'}
                      </code>
                    </td>
                    <td className="py-2 text-slate-400">
                      {e.playbook ?? '—'}
                    </td>
                    <td className="py-2">
                      <Badge
                        color={OUTCOME_COLORS[e.outcome] ?? 'slate'}
                        size="xs"
                      >
                        {e.outcome}
                      </Badge>
                    </td>
                    <td className="py-2 text-slate-300" title={e.error_message ?? undefined}>
                      {e.action_taken ?? (e.error_message ? 'erro' : '—')}
                    </td>
                    <td className="py-2 text-right text-slate-400">
                      {e.attempt}
                    </td>
                    <td className="py-2 text-right text-slate-400">
                      {e.duration_ms !== null ? `${e.duration_ms}ms` : '—'}
                    </td>
                    <td className="py-2">
                      {e.shadow_mode ? (
                        <Badge color="slate" size="xs">
                          shadow
                        </Badge>
                      ) : (
                        <Badge color="green" size="xs">
                          live
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
                {(!events.data || events.data.items.length === 0) && (
                  <tr>
                    <td
                      colSpan={9}
                      className="py-6 text-center text-slate-500"
                    >
                      Nenhum evento.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
